import React, { useContext, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CartContext } from "./CartContext";
import { CurrencyContext } from "./context/CurrencyContext";
import { api } from "./axiosClient";

const currencySymbols = {
  INR: "₹",
  USD: "$",
  EUR: "€",
  GBP: "£",
  AED: "د.إ",
};

const CartPage = () => {
  const { cart, increaseQty, decreaseQty, removeItem } = useContext(CartContext);
  const { getDeliveryFee } = useContext(CartContext);
  const { currency, rates, loading } = useContext(CurrencyContext);

  const [coupons, setCoupons] = useState([]);
  const [selectedCoupon, setSelectedCoupon] = useState(() => localStorage.getItem("applied_coupon_code") || "");
  const [discountValue, setDiscountValue] = useState(() => parseFloat(localStorage.getItem("applied_coupon_discount")) || 0);
  const navigate = useNavigate();

  // ✅ Stable convertPrice (fixes exhaustive-deps warning)
  const convertPrice = useCallback(
    (inrPrice) => {
      const rate = rates[currency];
      if (loading || !rate || inrPrice === null || inrPrice === undefined || isNaN(Number(inrPrice))) {
        return "...";
      }
      const converted = Number(inrPrice) * rate;
      return converted.toFixed(2);
    },
    [rates, currency, loading]
  );

  useEffect(() => {
    const fetchCoupons = async () => {
      try {
        const response = await api.get(`/api/coupons?currency=${currency}`);
        const list = Array.isArray(response.data)
          ? response.data
          : (response.data?.coupons || []);

        const now = new Date();
        const validCoupons = list.filter(
          (c) => c.active && (!c.expiry || new Date(c.expiry) > now)
        );

        setCoupons(validCoupons);
      } catch (error) {
        console.error("Failed to fetch coupons:", error);
      }
    };
    fetchCoupons();
  }, [currency]);

  useEffect(() => {
    const subtotalINR = cart.reduce((acc, item) => acc + item.price * item.quantity, 0);
    const coupon = coupons.find((c) => c.code === selectedCoupon);

    if (!coupon) {
      setDiscountValue(0);
      localStorage.setItem("applied_coupon_discount", "0");
      localStorage.removeItem("applied_coupon_code");
      localStorage.removeItem("applied_coupon_currency");
      return;
    }

    if (coupon.currency !== currency) {
      alert(`Coupon "${coupon.code}" is not valid for ${currency}`);
      setSelectedCoupon("");
      setDiscountValue(0);
      localStorage.removeItem("applied_coupon_discount");
      localStorage.removeItem("applied_coupon_code");
      localStorage.removeItem("applied_coupon_currency");
      return;
    }

    const minCartValueInINR = coupon.minCartValue || coupon.minAmount || 0;

    // If coupon currency is NOT INR, convert it to INR before comparing
    const requiredMinInINR = coupon.currency === "INR"
      ? minCartValueInINR
      : minCartValueInINR / (rates[coupon.currency] || 1);

    console.log("🛒 Subtotal INR:", subtotalINR);
    console.log("🔐 Required Min INR:", requiredMinInINR);

    if (subtotalINR < requiredMinInINR) {
      const convertedMin = convertPrice(requiredMinInINR);
      const symbol = currencySymbols[currency];
      alert(`Add at least ${symbol}${convertedMin} ${currency} to apply this coupon.`);
      setDiscountValue(0);
      localStorage.setItem("applied_coupon_discount", "0");
      localStorage.removeItem("applied_coupon_code");
      localStorage.removeItem("applied_coupon_currency");
      return;
    }

    const discountINR = coupon.type === "flat"
      ? coupon.amount
      : (subtotalINR * coupon.amount) / 100;

    setDiscountValue(discountINR);
    localStorage.setItem("applied_coupon_discount", discountINR.toString());
    localStorage.setItem("applied_coupon_code", coupon.code);
    localStorage.setItem("applied_coupon_currency", coupon.currency);
  }, [selectedCoupon, cart, coupons, currency, rates, loading, convertPrice]);

  const calculateTotal = () => {
    const subtotal = cart.reduce((acc, item) => acc + item.price * item.quantity, 0);
    return subtotal - discountValue + getDeliveryFee();
  };

  if (loading || !rates[currency]) {
    return (
      <div className="flex items-center justify-center min-h-screen text-lg font-medium">
        Loading cart...
      </div>
    );
  }

  return (
    <div className="bg-[#f3f0e4] min-h-screen px-4 sm:px-8 md:px-16 py-10 text-[#105132]">
      <h1 className="text-3xl font-bold mb-8 text-center">Your Shopping Cart</h1>

      <div className="flex flex-col md:flex-row gap-8">
        <div className="flex-1 bg-white rounded-2xl p-6 shadow-md">
          {cart.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <img src="/empty-cart-3d.png" alt="Empty Cart" className="w-48 mb-4 drop-shadow-lg" />
              <p className="text-lg text-gray-500 mb-4">Looks like you haven’t added anything yet.</p>
              <a href="/products" className="px-6 py-2 bg-[#105132] text-white rounded-full font-medium hover:bg-[#236e47] transition">
                Browse Products
              </a>
            </div>
          ) : (
            cart.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between mb-6 border-b pb-4">
                <div className="flex items-center gap-4">
                  <img src={item.image} alt={item.name} className="w-20 h-20 rounded-xl object-cover" />
                  <div>
                    <h2 className="font-bold text-lg">{item.name}</h2>
                    <p className="text-sm text-gray-500">
                      {currencySymbols[currency]}{convertPrice(item.price)} {currency}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => decreaseQty(item.name)} className="px-3 py-1 bg-[#236e47] text-white rounded-full text-lg">−</button>
                  <span>{item.quantity}</span>
                  <button onClick={() => increaseQty(item.name)} className="px-3 py-1 bg-[#236e47] text-white rounded-full text-lg">+</button>
                </div>
                <button onClick={() => removeItem(item.name)} className="text-red-500 text-xl">🗑</button>
              </div>
            ))
          )}
        </div>

        <div className="w-full md:w-[350px] bg-white rounded-2xl p-6 shadow-md">
          <h2 className="text-xl font-bold mb-4">Order Summary</h2>

          {cart.length > 0 && (
            <div className="mb-4">
              <label htmlFor="coupon" className="block text-sm font-medium mb-1">Apply Coupon</label>
              <select
                id="coupon"
                value={selectedCoupon}
                onChange={(e) => setSelectedCoupon(e.target.value)}
                className="w-full p-2 border rounded-md"
              >
                <option value="">-- Select a coupon --</option>
                {coupons.map((c) => {
                  const symbol = currencySymbols[currency] || "";
                  const type = String(c.type || "").toLowerCase();
                  const amount = Number(c.amount || 0);

                  const displayValue =
                    type === "percentage" || type === "percentage"
                      ? `${amount}%`
                      : `${symbol}${convertPrice(amount)} ${currency}`;

                  return (
                    <option key={c.code} value={c.code}>
                      {c.code} - {displayValue} off
                    </option>
                  );
                })}
              </select>
              <p className="text-xs text-gray-500 mt-1 italic">
                * Only showing coupons valid for {currency}
              </p>
            </div>
          )}

          <div className="flex justify-between mb-2">
            <span>Subtotal</span>
            <span>{currencySymbols[currency]}{convertPrice(cart.reduce((acc, item) => acc + item.price * item.quantity, 0))} {currency}</span>
          </div>

          <div className="flex justify-between mb-2 text-[#c0a230] font-medium">
            <span>Discount</span>
            <span>- {currencySymbols[currency]}{convertPrice(discountValue)} {currency}</span>
          </div>

          <div className="flex justify-between mb-4">
            <span>Delivery Fee</span>
            <span>{currencySymbols[currency]}{convertPrice(getDeliveryFee())} {currency}</span>
          </div>

          <div className="flex justify-between text-lg font-bold border-t pt-4">
            <span>Total</span>
            <span>{currencySymbols[currency]}{convertPrice(calculateTotal())} {currency}</span>
          </div>

          <button
            onClick={() => navigate("/checkout")}
            disabled={cart.length === 0}
            className={`w-full mt-6 py-3 font-semibold rounded-xl transition-all ${
              cart.length === 0
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-[#105132] text-white hover:bg-[#236e47]"
            }`}
          >
            Go to Checkout →
          </button>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
